// ============================================================
// Domain — Commission tiers and their baseline / floor rules
// ============================================================

import { ExposurePackage, resolveExposurePackage } from './exposurePackage';

export interface BaselineTier {
  id: string;
  category: string;
  baseline: number;
  unit: '$' | '%';
}

export interface TierRate extends BaselineTier {
  rate: number;
  isAdded?: boolean;
  _touched?: boolean;
}

export const makeBlankTiers = (baselineTiers: BaselineTier[]): TierRate[] =>
  baselineTiers.map((t) => ({ ...t, rate: t.baseline, _touched: false }));

/** The rate a tier lands on: the higher of the package floor and the tier's own baseline.
 *  Fixed ($) tiers are exempt — minCommissionRate is a commission-rate rule. */
export const floorForTier = (tier: BaselineTier, minCommissionRate: number): number =>
  tier.unit === '%' ? Math.max(minCommissionRate, Number(tier.baseline ?? 0)) : Number(tier.baseline ?? 0);

/** Re-derives every tier's rate from the package floor and the tier baseline.
 *
 *  Deliberately two-directional: an earlier version only raised rates that fell below the new floor,
 *  which left the previous package's higher rates in place when the merchant switched down (Flash Sale
 *  min 12% → Happy Hour min 4% kept 12%). Both the package switch and the brand switch route through
 *  here so the two entry points cannot drift apart. */
export const refreshTierRates = <T extends TierRate>(tiers: T[], minCommissionRate: number): T[] =>
  tiers.map((t) => ({ ...t, rate: floorForTier(t, minCommissionRate), _touched: false }));

// Any touched, baseline-violating tier blocks Continue. Mirrors prototype
// UpsizeWizard.jsx:198 — gates submission so the merchant fixes invalid rates
// before reaching Review (rather than discovering it at submit time).
export const tiersHaveBaselineViolation = (tiers: TierRate[]): boolean =>
  tiers.some((t) => !t.isAdded && t._touched && t.baseline !== null && Number(t.rate) < Number(t.baseline));

/** True when any %-tier commission rate is below the package's minCommissionRate floor.
 *  No _touched guard — it's an absolute floor; auto-fill pre-satisfies it on selection,
 *  so this only bites when the merchant manually edits a %-tier back below the minimum. */
export const tiersBelowPackageMinimum = (
  tiers: TierRate[],
  packageRef: string | null,
  packages: ExposurePackage[],
  country?: string
): boolean => {
  const min = resolveExposurePackage(packages, packageRef, country)?.minCommissionRate || 0;
  if (min <= 0) return false;
  return tiers.some((t) => t.unit === '%' && Number(t.rate) < min);
};
